import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const pendingVariantIds = new Set();
// Line keys with an auto-removal in flight, shared by every instance so one key is only removed once.
const removingKeys = new Set();

/**
 * Store customization — free-item opt-in row (Summum Woman).
 *
 * Toggles a free (€0) reward product in/out of the cart from an add button (reward-tier style). Adds it with a
 * `_reward_tier` line-item property (so cart-products.liquid hides its price/qty/size) and
 * removes it on uncheck. Also auto-removes rewards flagged via `data-remove-keys` when the
 * cart is no longer eligible (server-rendered after a cart change morph).
 *
 * @extends {Component}
 */
class ApcFreeItems extends Component {
  static observedAttributes = ['data-remove-keys'];
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);

    const keys = this.dataset.removeKeys;
    if (keys) this.#removeKeys(keys.split(','));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    for (const row of this.querySelectorAll('.apc-free-items__row')) {
      if (row instanceof HTMLElement && pendingVariantIds.has(row.dataset.variantId)) {
        row.toggleAttribute('data-claimed', true);
      }
    }
  }

  /**
   * Auto-remove reward line items when the section re-renders with keys to strip.
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-remove-keys' && newValue && !this.#busy) {
      this.#removeKeys(newValue.split(','));
    }
  }

  /**
   * Add the free reward when its "add" button (styled like the reward tiers) is clicked.
   * Once added the row shows the checkmark instead; removal is via the cart line item (and
   * the auto-remove path when the cart is no longer eligible).
   * @param {Event} event
   */
  #onClick = async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const button = target.closest('.cart-reward-tiers__add-button');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const item = button.closest('.apc-free-items__row');
    if (!(item instanceof HTMLElement) || item.hasAttribute('data-claimed')) return;

    const { variantId, tierLabel } = item.dataset;
    if (!variantId || pendingVariantIds.has(variantId)) return;

    item.toggleAttribute('data-claimed', true);
    pendingVariantIds.add(variantId);
    const added = await this.#add(variantId, tierLabel || 'reward');
    pendingVariantIds.delete(variantId);
    if (!added) item.toggleAttribute('data-claimed', false);
  };

  /**
   * Section ids of every cart-items-component, so a change returns their rendered HTML in
   * one round-trip (drawer / cart page morph instead of a re-fetch).
   * @returns {string} Comma-separated section ids, or '' if none.
   */
  #cartSectionIds() {
    const ids = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    }
    return ids.join(',');
  }

  /**
   * Adds the free reward variant to the cart.
   * @param {string} variantId
   * @param {string} tierLabel
   * @returns {Promise<boolean>} Whether the add succeeded
   */
  async #add(variantId, tierLabel) {
    const sections = this.#cartSectionIds();
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1, properties: { _reward_tier: tierLabel } }],
      sections: sections || undefined,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, { ...fetchConfig('json', { body }) });
      const result = await response.json();

      // Shopify flags add failures via a `status` field, even on a 200.
      if (!response.ok || result.status) {
        console.error('[apc-free-items] Failed to add', result.message || response.status);
        return false;
      }

      const { item_count } = await fetch(Theme.routes.cart_url + '.js').then((r) => r.json());

      document.dispatchEvent(
        new CartAddEvent(result, 'apc-free-items', {
          source: 'apc-free-items',
          itemCount: item_count,
          sections: result.sections,
        }),
      );
      return true;
    } catch (error) {
      console.error('[apc-free-items]', error);
      return false;
    }
  }

  /**
   * Removes reward line items by key and dispatches a cart update.
   * @param {string[]} keys
   */
  async #removeKeys(keys) {
    // On upgrade both attributeChangedCallback and connectedCallback land here for the same keys;
    // a second update for an already-removed key returns 422.
    const pending = keys.filter((key) => key && !removingKeys.has(key));
    if (!pending.length) return;
    for (const key of pending) removingKeys.add(key);
    this.#busy = true;

    try {
      /** @type {Record<string, number>} */
      const updates = {};
      for (const key of pending) updates[key] = 0;

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ updates }) }),
      });

      if (!response.ok) {
        console.error('[apc-free-items] Failed to remove', response.status);
        return;
      }

      const cart = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'apc-free-items', {
          source: 'apc-free-items',
          itemCount: cart.item_count,
        }),
      );
    } catch (error) {
      console.error('[apc-free-items]', error);
    } finally {
      this.#busy = false;
      for (const key of pending) removingKeys.delete(key);
    }
  }
}

if (!customElements.get('apc-free-items')) {
  customElements.define('apc-free-items', ApcFreeItems);
}
